import { useState } from 'react';
import type { VatReturnResponse } from '@hesabat/contracts';
import { Money } from '@hesabat/finance-core';
import type { CsvCell } from './csv';
import { formatMoney, sumAmounts } from './money';

type SalesRow = VatReturnResponse['sales'][number];
type Treatment = SalesRow['treatment'];
type DocumentKind = VatReturnResponse['documents'][number]['kind'];

const TREATMENT: Record<Treatment, string> = {
  standard: 'قياسى',
  zero: 'صفرى',
  exempt: 'معفى',
  out_of_scope: 'خارج النطاق',
  table: 'ضريبة الجدول',
  withholding: 'خصم من المنبع',
};

const KIND: Record<DocumentKind, string> = {
  sale: 'فاتورة مبيعات',
  credit_note: 'إشعار دائن',
  debit_note: 'إشعار مدين',
  purchase: 'مشتريات',
  other: 'أخرى',
};

/** النسبة نص من الأثر المحفوظ (`14.0000`) — تُعرض بلا أصفار زائدة، بلا حساب. */
function rate(ratePercent: string): string {
  return ratePercent.replace(/\.?0+$/, '') + '٪';
}

function ruleLabel(row: { treatment: Treatment; ratePercent: string }): string {
  return row.treatment === 'standard' || row.treatment === 'table'
    ? TREATMENT[row.treatment] + ' ' + rate(row.ratePercent)
    : TREATMENT[row.treatment];
}

interface Selection {
  rowKey: string;
  kinds: readonly DocumentKind[];
  title: string;
  /** خانة الصافى: الإشعار الدائن يُطرح فيه، فيُعرض سالبًا ليساوى مجموع المستندات الرقم. */
  netting?: boolean;
}

/** رقم يُضغط فتظهر مستنداته — كل رقم فى الملخص يُتتبَّع إلى ما رُحّل. */
function Figure({
  amount,
  selection,
  onSelect,
}: {
  amount: string;
  selection: Selection;
  onSelect: (selection: Selection) => void;
}) {
  return (
    <td className="num-cell">
      <button type="button" className="link" onClick={() => onSelect(selection)}>
        {formatMoney(amount)}
      </button>
    </td>
  );
}

/**
 * ملخص إقرار القيمة المضافة — الأرقام من الـ API كما هى، والواجهة تعرض وتصفّى المستندات فقط.
 *
 * `BLOCKED-TAX`: ملخص داخلى يساعد فى ملء الإقرار الرسمى، لا نموذجه. ترتيب البنود وتسميتها
 * تنتظر مستشار الضرائب، والشاشة تقول ذلك صراحة للمستخدم.
 */
export default function VatReturn({ vat, currency }: { vat: VatReturnResponse; currency: string }) {
  const [selection, setSelection] = useState<Selection | null>(null);
  const taxable = vat.sales.filter((row) => row.taxable);
  const nonTaxable = vat.sales.filter((row) => !row.taxable);
  const documents = selection
    ? vat.documents
        .filter((doc) => doc.rowKey === selection.rowKey && selection.kinds.includes(doc.kind))
        .map((doc) =>
          selection.netting && doc.kind === 'credit_note'
            ? {
                ...doc,
                base: Money.of(doc.base).negate().toString(),
                tax: Money.of(doc.tax).negate().toString(),
              }
            : doc,
        )
    : [];
  const select = (next: Selection) => setSelection(next);
  const salesKinds = (row: SalesRow, title: string, kinds: readonly DocumentKind[]) => ({
    rowKey: row.key,
    kinds,
    title: ruleLabel(row) + ' — ' + title,
    netting: kinds.length > 1,
  });

  return (
    <>
      <p className="notice">
        ملخص داخلى من القيود المنشورة يساعد فى ملء الإقرار الرسمى — ليس نموذج الإقرار ولا يعنى
        امتثالاً ضريبيًا. ترتيب البنود ومعاملة الإلغاءات وضريبة الجدول تنتظر مراجعة مستشار الضرائب.
      </p>

      <div className="cards">
        <div className="card">
          <div className="label">ضريبة المخرجات (بعد الإشعارات)</div>
          <div className="num">{formatMoney(vat.outputTax)}</div>
        </div>
        <div className="card">
          <div className="label">ضريبة المدخلات القابلة للخصم</div>
          <div className="num">{formatMoney(vat.recoverableInputTax)}</div>
        </div>
        <div className="card">
          <div className="label">
            {vat.position === 'refundable'
              ? 'رصيد لصالح الشركة'
              : vat.position === 'payable'
                ? 'الصافى المستحق للتوريد'
                : 'الصافى'}
          </div>
          <div className="num">{formatMoney(vat.netTax)}</div>
        </div>
        <div className="card">
          <div className="label">مدخلات غير قابلة للخصم (حُمّلت على التكلفة)</div>
          <div className="num">{formatMoney(vat.nonRecoverableInputTax)}</div>
        </div>
      </div>

      <h3>المبيعات الخاضعة</h3>
      {taxable.length === 0 ? (
        <p className="sub">لا مبيعات خاضعة فى هذه الفترة.</p>
      ) : (
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th rowSpan={2}>القاعدة</th>
                <th colSpan={2}>المبيعات</th>
                <th colSpan={2}>الإشعارات الدائنة</th>
                <th colSpan={2}>الإشعارات المدينة</th>
                <th colSpan={2}>الصافى</th>
              </tr>
              <tr>
                {[0, 1, 2, 3].map((index) => (
                  <FigureHead key={index} />
                ))}
              </tr>
            </thead>
            <tbody>
              {taxable.map((row) => (
                <tr key={row.key}>
                  <td>{ruleLabel(row)}</td>
                  {(
                    [
                      [row.sales, 'المبيعات', ['sale']],
                      [row.creditNotes, 'الإشعارات الدائنة', ['credit_note']],
                      [row.debitNotes, 'الإشعارات المدينة', ['debit_note']],
                      [row.net, 'الصافى', ['sale', 'credit_note', 'debit_note']],
                    ] as const
                  ).flatMap(([figure, title, kinds]) => [
                    <Figure
                      key={title + '-base'}
                      amount={figure.base}
                      selection={salesKinds(row, title, kinds)}
                      onSelect={select}
                    />,
                    <Figure
                      key={title + '-tax'}
                      amount={figure.tax}
                      selection={salesKinds(row, title, kinds)}
                      onSelect={select}
                    />,
                  ])}
                </tr>
              ))}
              <tr className="subtotal">
                <td colSpan={7}>إجمالى المبيعات الخاضعة وضريبة المخرجات</td>
                <td className="num-cell">{formatMoney(vat.taxableSalesBase)}</td>
                <td className="num-cell">{formatMoney(vat.outputTax)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      <h3>المبيعات الصفرية والمعفاة وخارج النطاق</h3>
      {nonTaxable.length === 0 ? (
        <p className="sub">لا شىء فى هذه الفترة.</p>
      ) : (
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>المعالجة</th>
                <th>المبيعات</th>
                <th>الإشعارات الدائنة</th>
                <th>الإشعارات المدينة</th>
                <th>صافى الوعاء</th>
              </tr>
            </thead>
            <tbody>
              {nonTaxable.map((row) => (
                <tr key={row.key}>
                  <td>{ruleLabel(row)}</td>
                  <Figure
                    amount={row.sales.base}
                    selection={salesKinds(row, 'المبيعات', ['sale'])}
                    onSelect={select}
                  />
                  <Figure
                    amount={row.creditNotes.base}
                    selection={salesKinds(row, 'الإشعارات الدائنة', ['credit_note'])}
                    onSelect={select}
                  />
                  <Figure
                    amount={row.debitNotes.base}
                    selection={salesKinds(row, 'الإشعارات المدينة', ['debit_note'])}
                    onSelect={select}
                  />
                  <Figure
                    amount={row.net.base}
                    selection={salesKinds(row, 'الصافى', ['sale', 'credit_note', 'debit_note'])}
                    onSelect={select}
                  />
                </tr>
              ))}
              <tr className="subtotal">
                <td colSpan={4}>الإجمالى</td>
                <td className="num-cell">{formatMoney(vat.nonTaxableSalesBase)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      <h3>المشتريات وضريبة المدخلات</h3>
      {vat.purchases.length === 0 ? (
        <p className="sub">لا مشتريات بأثر ضريبى فى هذه الفترة.</p>
      ) : (
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>القاعدة</th>
                <th>الخصم</th>
                <th>الوعاء</th>
                <th>الضريبة</th>
              </tr>
            </thead>
            <tbody>
              {vat.purchases.map((row) => {
                const rowSelection = {
                  rowKey: row.key,
                  kinds: ['purchase'] as const,
                  title: ruleLabel(row) + (row.recoverable ? ' — قابلة للخصم' : ' — غير قابلة'),
                };
                return (
                  <tr key={row.key}>
                    <td>{ruleLabel(row)}</td>
                    <td>{row.recoverable ? 'قابلة للخصم' : 'غير قابلة (على التكلفة)'}</td>
                    <Figure
                      amount={row.purchases.base}
                      selection={rowSelection}
                      onSelect={select}
                    />
                    <Figure amount={row.purchases.tax} selection={rowSelection} onSelect={select} />
                  </tr>
                );
              })}
              <tr className="subtotal">
                <td colSpan={2}>الإجمالى</td>
                <td className="num-cell">{formatMoney(vat.purchasesBase)}</td>
                <td className="num-cell">
                  {formatMoney(sumAmounts([vat.recoverableInputTax, vat.nonRecoverableInputTax]))}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {vat.other.length > 0 && (
        <>
          <h3>أثر ضريبى خارج الملخص</h3>
          <p className="error">
            قيود تحمل أثرًا ضريبيًا ولا يُعرف موضعها فى الإقرار (قيود يدوية مثلاً) — معروضة هنا ولم
            تدخل الصافى. راجعها مع المحاسب.
          </p>
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>نوع القيد</th>
                  <th>القاعدة</th>
                  <th>دائن: وعاء</th>
                  <th>دائن: ضريبة</th>
                  <th>مدين: وعاء</th>
                  <th>مدين: ضريبة</th>
                </tr>
              </thead>
              <tbody>
                {vat.other.map((row) => {
                  const rowSelection = {
                    rowKey: row.key,
                    kinds: ['other'] as const,
                    title: row.sourceType + ' — ' + ruleLabel(row),
                  };
                  return (
                    <tr key={row.key}>
                      <td>{row.sourceType}</td>
                      <td>{ruleLabel(row)}</td>
                      <Figure amount={row.credit.base} selection={rowSelection} onSelect={select} />
                      <Figure amount={row.credit.tax} selection={rowSelection} onSelect={select} />
                      <Figure amount={row.debit.base} selection={rowSelection} onSelect={select} />
                      <Figure amount={row.debit.tax} selection={rowSelection} onSelect={select} />
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}

      {selection && (
        <div className="panel">
          <div className="panel-head">
            <h3>مستندات: {selection.title}</h3>
            <button type="button" className="ghost no-print" onClick={() => setSelection(null)}>
              إغلاق
            </button>
          </div>
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>التاريخ</th>
                  <th>القيد</th>
                  <th>المرجع</th>
                  <th>المستند</th>
                  <th>الوعاء</th>
                  <th>الضريبة</th>
                </tr>
              </thead>
              <tbody>
                {documents.map((doc) => (
                  <tr key={doc.journalId + doc.rowKey}>
                    <td>{doc.postingDate}</td>
                    <td>{doc.journalNumber}</td>
                    <td>{doc.reference}</td>
                    <td>
                      {KIND[doc.kind]}
                      {doc.journalSourceType === 'reversal' ? ' — إلغاء بقيد عكسى' : ''}
                    </td>
                    <td className="num-cell">{formatMoney(doc.base)}</td>
                    <td className="num-cell">{formatMoney(doc.tax)}</td>
                  </tr>
                ))}
                <tr className="subtotal">
                  <td colSpan={4}>الإجمالى ({documents.length} مستند)</td>
                  <td className="num-cell">
                    {formatMoney(sumAmounts(documents.map((doc) => doc.base)))}
                  </td>
                  <td className="num-cell">
                    {formatMoney(sumAmounts(documents.map((doc) => doc.tax)))}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

      <p className="label">
        المبالغ بـ {currency}، من القيود المنشورة وحدها. اضغط أى رقم لعرض مستنداته.
      </p>
    </>
  );
}

function FigureHead() {
  return (
    <>
      <th>الوعاء</th>
      <th>الضريبة</th>
    </>
  );
}

export const VAT_HEADER = ['البند', 'القاعدة', 'الخانة', 'الوعاء', 'الضريبة'];

/** صفوف CSV: كل رقم فى الملخص بسطر، ثم المستندات — المبالغ نصوص العقد كما هى. */
export function vatRows(vat: VatReturnResponse): CsvCell[][] {
  const rows: CsvCell[][] = [];
  for (const row of vat.sales) {
    const section = row.taxable ? 'مبيعات خاضعة' : 'مبيعات صفرية/معفاة';
    rows.push([section, ruleLabel(row), 'المبيعات', row.sales.base, row.sales.tax]);
    rows.push([
      section,
      ruleLabel(row),
      'الإشعارات الدائنة',
      row.creditNotes.base,
      row.creditNotes.tax,
    ]);
    rows.push([
      section,
      ruleLabel(row),
      'الإشعارات المدينة',
      row.debitNotes.base,
      row.debitNotes.tax,
    ]);
    rows.push([section, ruleLabel(row), 'الصافى', row.net.base, row.net.tax]);
  }
  for (const row of vat.purchases) {
    rows.push([
      'مشتريات',
      ruleLabel(row),
      row.recoverable ? 'قابلة للخصم' : 'غير قابلة للخصم',
      row.purchases.base,
      row.purchases.tax,
    ]);
  }
  for (const row of vat.other) {
    rows.push([
      'خارج الملخص: ' + row.sourceType,
      ruleLabel(row),
      'دائن',
      row.credit.base,
      row.credit.tax,
    ]);
    rows.push([
      'خارج الملخص: ' + row.sourceType,
      ruleLabel(row),
      'مدين',
      row.debit.base,
      row.debit.tax,
    ]);
  }
  rows.push(['الإجمالى', '', 'ضريبة المخرجات', vat.taxableSalesBase, vat.outputTax]);
  rows.push(['الإجمالى', '', 'المدخلات القابلة للخصم', '', vat.recoverableInputTax]);
  rows.push(['الإجمالى', '', 'المدخلات غير القابلة للخصم', '', vat.nonRecoverableInputTax]);
  rows.push(['الإجمالى', '', 'الصافى', '', vat.netTax]);
  rows.push([]);
  rows.push(['المستند', 'التاريخ', 'القيد', 'المرجع', 'البند', 'الوعاء', 'الضريبة']);
  for (const doc of vat.documents) {
    rows.push([
      KIND[doc.kind] + (doc.journalSourceType === 'reversal' ? ' (إلغاء)' : ''),
      doc.postingDate,
      doc.journalNumber,
      doc.reference,
      doc.rowKey,
      doc.base,
      doc.tax,
    ]);
  }
  return rows;
}
